// packages/composables/src/common/useLoading.ts
import {computed, ref} from "@vue/composition-api";
var useLoading = () => {
  const loadingStack = ref([]);
  const isLoading = computed(() => !!loadingStack.value.length);
  function getIsLoading(name) {
    if (!name)
      throw new Error("Name is required");
    return computed(() => loadingStack.value.includes(name));
  }
  function getIsLoadingSome(names) {
    if (!names)
      throw new Error("Names are required");
    return computed(() => names.some((name) => loadingStack.value.includes(name)));
  }
  function startLoading(name) {
    loadingStack.value.push(name);
  }
  function startLoadingUnless(name, condition) {
    const shouldStartLoading = typeof condition === "function" ? !condition() : !condition;
    if (shouldStartLoading)
      startLoading(name);
  }
  function stopLoading(name) {
    if (!name)
      throw new Error("Name is required");
    const index = loadingStack.value.indexOf(name);
    loadingStack.value.splice(index, 1);
  }
  return {
    isLoading,
    getIsLoading,
    getIsLoadingSome,
    startLoading,
    startLoadingUnless,
    stopLoading
  };
};

// packages/composables/src/common/useDocumentTypes.ts
import {computed as computed2, ref as ref2} from "@vue/composition-api";
import {getDocumentTypes} from "@/api";
import _isArray from "lodash/isArray";

// packages/composables/src/types/DocumentTypes.ts
import {SYSTEM_DOCUMENT_TYPES_MAP} from "@shared/constants/requestTypes";
function getSystemDocumentTypes(productCode) {
  return SYSTEM_DOCUMENT_TYPES_MAP[productCode] || [];
}
function getSystemRequestActions() {
  return [
    {
      name: "New",
      id: "new"
    },
    {
      name: "Edit",
      id: "edit"
    },
    {
      name: "Delete",
      id: "delete"
    }
  ];
}

// packages/composables/src/common/useDocumentTypes.ts
var customDocumentsTypesCache = ref2([]);
function useDocumentTypes({
  school,
  product,
  currentTypeId = ref2()
}) {
  const isCurrentTypeArray = computed2(() => Array.isArray(currentTypeId.value));
  const currentTypeIdNormalized = computed2(() => isCurrentTypeArray.value ? currentTypeId.value : [currentTypeId.value].filter(Boolean));
  const currentDocumentTypeNormalized = computed2(() => {
    if (_isArray(currentTypeIdNormalized.value)) {
      return currentTypeIdNormalized.value.map((id) => availableDocumentTypes.value.find((documentType) => documentType.id === id)).filter(Boolean);
    }
  });
  const currentDocumentType = computed2(() => isCurrentTypeArray.value ? currentDocumentTypeNormalized.value : currentDocumentTypeNormalized.value ? currentDocumentTypeNormalized.value[0] : void 0);
  const systemDocumentTypes = computed2(() => getSystemDocumentTypes(product.value));
  const customDocumentTypes = computed2(() => customDocumentsTypesCache.value || []);
  async function loadDocumentTypes({useCache = false} = {}) {
    if (useCache && customDocumentsTypesCache.value?.length)
      return;
    const response = await getDocumentTypes({school});
    if (response instanceof Error)
      throw response;
    if (typeof response === "symbol")
      throw response;
    if (response.error instanceof Error)
      throw response.error;
    customDocumentsTypesCache.value = response.data.map((documentType) => {
      if (documentType.deletedAt) {
        documentType.name = `${documentType.name} (deleted document)`;
      }
      return documentType;
    });
  }
  loadDocumentTypes({useCache: true});
  const availableDocumentTypes = computed2(() => {
    if (product.value === "cm") {
      return [
        ...systemDocumentTypes.value,
        ...customDocumentTypes.value.filter((documentType) => documentType.product === "cm" || !documentType.product)
      ];
    }
    return [
      ...systemDocumentTypes.value,
      ...customDocumentTypes.value.filter((documentType) => documentType.product === product.value)
    ];
  });
  function _getRequestTypesFromDocument(documentType) {
    if (documentType && !documentType.requestTypes) {
      return [documentType];
    }
    return documentType?.requestTypes || [];
  }
  const _availableProductTypes = computed2(() => availableDocumentTypes.value.flatMap((documentType) => _getRequestTypesFromDocument(documentType)));
  const availableRequestTypes = computed2(() => {
    if (currentDocumentTypeNormalized.value?.length)
      return currentDocumentTypeNormalized.value.flatMap(_getRequestTypesFromDocument);
    return _availableProductTypes.value;
  });
  const requestActions = computed2(() => getSystemRequestActions());
  return {
    availableDocumentTypes,
    availableRequestTypes,
    loadDocumentTypes,
    currentTypeId,
    currentDocumentType,
    currentDocumentTypeNormalized,
    requestActions
  };
}

// packages/composables/src/common/useModals.ts
import {computed as computed3, ref as ref3} from "@vue/composition-api";
var useModals = () => {
  const modalStack = ref3([]);
  function isModalVisible(name) {
    if (!name)
      throw new Error("Name is required");
    return computed3(() => modalStack.value.includes(name));
  }
  function openModal(name) {
    if (!name)
      throw new Error("Name is required");
    if (modalStack.value.includes(name))
      return;
    modalStack.value.push(name);
  }
  function closeLastModal() {
    modalStack.value.pop();
  }
  function closeUntilModal(name) {
    if (!name)
      throw new Error("Name is required");
    const index = modalStack.value.indexOf(name);
    if (index === -1)
      return;
    modalStack.value.splice(index, modalStack.value.length - index);
  }
  function closeModalByName(name) {
    if (!name)
      throw new Error("Name is required");
    const index = modalStack.value.indexOf(name);
    modalStack.value.splice(index, 1);
  }
  function closeAllModals() {
    modalStack.value = [];
  }
  return {
    isModalVisible,
    openModal,
    closeLastModal,
    closeModalByName,
    closeAllModals,
    closeUntilModal
  };
};
export {
  useDocumentTypes,
  useLoading,
  useModals
};
